import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { generateBreadcrumbList } from '../utils/schemaHelpers';

const faqs: { q: string; a: React.ReactNode }[] = [
  {
    q: 'How does the AI assistant work?',
    a: (
      <>
        The AI assistant uses Cloudflare Workers AI (Llama 3.3 70B) running inside Cloudflare's
        edge infrastructure. Your plain-English question is parsed by the LLM into structured JSON
        — service names, quantities, tiers, and regions — and that JSON is validated against a
        strict whitelist before it reaches the pricing layer. Your raw message text never touches
        the pricing API.
        <br /><br />
        This architecture is called a <strong style={{ color: 'var(--text-secondary)' }}>firewall pattern</strong>:
        the chat worker is the only entry point for free text. It sanitises, validates, and
        transforms the LLM output before any price calculation happens. Prompt injection attempts
        are rejected at the input stage by a 20+ pattern rejection list. Unknown service names in
        the LLM output reject the entire response, not just the offending entry.
        <br /><br />
        For multi-service questions, the assistant keeps you on the /ai page and shows a combined
        estimate. Follow-up adjustments ("make it 80 GB/day") are sent as a second LLM call with
        the prior parsed state as context — the model returns the full updated state and prices
        are recalculated. No conversation history is stored server-side; the LLM context is
        session-only and not retained between page loads.
        <br /><br />
        No data leaves Cloudflare's network. The model runs on Cloudflare Workers AI, not an
        external API. There are no OpenAI or Anthropic API calls behind the assistant.
      </>
    ),
  },
  {
    q: 'Is this a replacement for the official Azure Pricing Calculator?',
    a: (
      <>
        No. AzureCalc.uk is built for the specific scenarios Microsoft's calculator handles
        poorly: combined Log Analytics + Sentinel estimates in a single result, commitment
        tier break-even analysis with a visual crossover chart, GBP-native pricing without
        manual currency switching each session, and KQL queries to verify your actual spend
        against estimates. For comprehensive multi-service estimation across 200+ Azure
        services, use the{' '}
        <a
          href="https://azure.microsoft.com/pricing/calculator/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--text-tertiary)' }}
        >
          official Azure Pricing Calculator
        </a>
        .
      </>
    ),
  },
  {
    q: 'How accurate are the prices on AzureCalc.uk?',
    a: (
      <>
        Prices are fetched nightly from the official Azure Retail Prices API (prices.azure.com)
        for UK South in GBP. They reflect retail/pay-as-you-go rates. Enterprise Agreement, CSP,
        and reserved instance discounts are not applied — your actual contracted price will likely
        be lower. Always verify final pricing via the official Azure pricing calculator before
        committing to a purchase decision.
      </>
    ),
  },
  {
    q: 'What is an EA, CSP, or MACC discount and how do I apply it to my estimate?',
    a: (
      <>
        Enterprise Agreement (EA), Cloud Solution Provider (CSP), and Microsoft Azure
        Consumption Commitment (MACC) are commercial agreements that give discounts of
        typically 15–50% off retail PAYG rates. AzureCalc.uk shows retail (PAYG) prices
        by default. Use the <strong style={{ color: 'var(--text-secondary)' }}>"Apply EA/CSP/MACC discount"</strong> field
        in the Advanced section of any calculator result to enter your negotiated discount
        percentage and see the adjusted estimate alongside the PAYG figure. Your actual
        contracted rate will be in your Microsoft agreement or CSP partner portal.
      </>
    ),
  },
  {
    q: 'Why is my actual Azure bill different from the estimate?',
    a: (
      <>
        Several factors can cause a difference: Enterprise Agreement or CSP discounts, free tier
        credits, reserved instance savings, Azure Hybrid Benefit, Dev/Test pricing, and the fact
        that Azure bills some services at a granularity that differs from the estimate inputs.
        Estimates are a starting point for planning and comparison — not a billing guarantee.
      </>
    ),
  },
  {
    q: 'What is the Azure Log Analytics 2TB restore minimum?',
    a: (
      <>
        When you restore archived Log Analytics data, Azure bills a minimum of 2TB (2,048 GB)
        regardless of actual data size. Restoring 3GB costs exactly the same as restoring
        2,048GB — £189.24/day at current UK South rates. The minimum only stops mattering once
        your actual data exceeds 2TB.{' '}
        <Link
          to="/guides/log-analytics-search-job-vs-restore"
          style={{ color: 'var(--text-tertiary)' }}
        >
          See our full explanation and cost comparison →
        </Link>
      </>
    ),
  },
  {
    q: 'What is the difference between a Search Job and a Restore in Log Analytics?',
    a: (
      <>
        Search Jobs query archived data asynchronously and write results to a{' '}
        <code className="px-1 rounded text-[12px]" style={{ color: '#c7e0f4', backgroundColor: 'var(--surface-container-low)' }}>_SRCH</code>{' '}
        table — charged at £0.0046/GB scanned with no minimum. Restore brings archived data into
        a hot cache for repeated KQL querying — charged at £0.0924/GB/day with a 2TB minimum.
        For ad-hoc investigations use Search Jobs. Only use Restore when you need to run 50+
        queries against the same dataset over multiple days.{' '}
        <Link
          to="/guides/log-analytics-search-job-vs-restore"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Full comparison guide →
        </Link>
      </>
    ),
  },
  {
    q: 'What is NAT Gateway always-on billing and why does it matter?',
    a: (
      <>
        Unlike most Azure resources, NAT Gateway cannot be stopped or paused — only deleted.
        It bills every hour it exists, regardless of whether any traffic flows through it.
        At UK South rates, an idle NAT Gateway costs approximately £33/month. Engineers
        frequently deploy one for testing and forget to delete it. Use the{' '}
        <Link to="/networking" style={{ color: 'var(--text-tertiary)' }}>
          Networking calculator
        </Link>{' '}
        to estimate your NAT Gateway costs, and set a budget alert if you deploy one for
        temporary use.
      </>
    ),
  },
  {
    q: 'Why does Azure charge for outbound data but not inbound?',
    a: (
      <>
        This is standard cloud industry practice. Inbound data helps providers sell services
        (you are storing more with them), so it is free. Outbound data consumes network
        bandwidth that Microsoft must pay for and manage. The first 100GB/month of outbound
        internet egress from Azure is free — charges apply above that threshold.
      </>
    ),
  },
  {
    q: 'What is the difference between LRS, ZRS, GRS and RA-GRS storage?',
    a: (
      <>
        <strong style={{ color: 'var(--text-secondary)' }}>LRS</strong> stores 3 copies within a single
        datacentre — cheapest, but a datacentre failure could cause data loss.{' '}
        <strong style={{ color: 'var(--text-secondary)' }}>ZRS</strong> stores 3 copies across 3 availability
        zones — protects against datacentre failure, ~25% more than LRS.{' '}
        <strong style={{ color: 'var(--text-secondary)' }}>GRS</strong> stores 6 copies across 2 regions (UK
        South + UK West) — protects against regional failure, ~2× LRS cost.{' '}
        <strong style={{ color: 'var(--text-secondary)' }}>RA-GRS</strong> is GRS with a readable secondary
        region. For production workloads ZRS is the minimum recommended option.
      </>
    ),
  },
  {
    q: 'When should I use Azure Blob Storage Archive tier?',
    a: (
      <>
        Archive is designed for data you rarely or never access — compliance archives, legal
        holds, and long-term backups you are required to keep but will almost certainly never
        read. Data in Archive is offline and must be rehydrated (which takes hours) before it
        can be read. A 180-day minimum retention period applies — early deletion is penalised.
        Never place data in Archive if you might need it quickly or access it more than once or
        twice a year.
      </>
    ),
  },
  {
    q: 'What is a Log Analytics commitment tier and when is it worth it?',
    a: (
      <>
        Commitment tiers are reserved daily ingestion capacity (100–500 GB/day) that offer up to
        25% savings compared to PAYG rates. The 100 GB/day commitment tier breaks even at
        approximately 90 GB/day of billable ingestion. If your workspace consistently ingests
        above 90 GB/day, the 100 GB commitment tier will save money. Use our Log Analytics
        calculator to find the break-even point for your ingestion volume.
      </>
    ),
  },
  {
    q: 'Does enabling Microsoft Sentinel increase my Log Analytics costs?',
    a: (
      <>
        Yes. Sentinel adds a per-GB charge on top of Log Analytics ingestion. The combined
        PAYG rate is approximately £6.10/GB. However, free data allowances offset this for
        many deployments: Microsoft 365 E5 licences include 5 MB/user/day free, and Defender
        for Servers P2 includes 500 MB/server/day free. The 31-day free trial for new Sentinel
        workspaces also helps evaluate cost before committing.
      </>
    ),
  },
  {
    q: 'How do I report a pricing error on AzureCalc.uk?',
    a: (
      <>
        Use the ❓ feedback button on any calculator results panel to report a pricing issue.
        If the estimate looks wrong, first use the <strong style={{ color: 'var(--text-secondary)' }}>Save &amp; Share Estimate</strong> button
        to save the estimate — this captures the exact inputs and result so our team can
        reproduce the issue. We review all reports within 48 hours. Prices are refreshed
        nightly from the Azure Retail Prices API so most discrepancies resolve automatically
        overnight.
      </>
    ),
  },
  {
    q: 'Is AzureCalc.uk affiliated with Microsoft?',
    a: (
      <>
        No. AzureCalc.uk is an independent tool built for UK Azure practitioners. Prices are
        sourced from Microsoft's public Azure Retail Prices API. AzureCalc.uk is not endorsed
        by, affiliated with, or sponsored by Microsoft Corporation. Microsoft Azure and related
        product names are trademarks of Microsoft Corporation.
      </>
    ),
  },
  {
    q: 'Can I save and share my estimates?',
    a: (
      <>
        Yes — use the <strong style={{ color: 'var(--text-secondary)' }}>Save &amp; Share Estimate</strong> button
        on any calculator results panel. This saves your estimate to a unique URL
        (e.g. <code className="px-1 rounded text-[12px]" style={{ color: '#c7e0f4', backgroundColor: 'var(--surface-container-low)' }}>azure-calc.co.uk/estimate/abc123</code>)
        that you can share with colleagues. Estimates are stored for 90 days. Your saved
        estimates are also listed at{' '}
        <Link to="/saved" style={{ color: 'var(--text-tertiary)' }}>
          azure-calc.co.uk/saved
        </Link>
        {' '}— these are stored in your browser and are device-specific.
      </>
    ),
  },
  {
    q: 'What Azure regions does AzureCalc.uk cover?',
    a: (
      <>
        Currently UK South only — the primary Azure region for most UK organisations. All prices
        are fetched with <code className="px-1 rounded text-[12px] text-[#0078d4]" style={{ backgroundColor: 'var(--surface-container-high)' }}>armRegionName eq 'uksouth'</code> and{' '}
        <code className="px-1 rounded text-[12px] text-[#0078d4]" style={{ backgroundColor: 'var(--surface-container-high)' }}>currencyCode eq 'GBP'</code> from the Azure Retail Prices API.
        Multi-region support is planned for a future update.
      </>
    ),
  },
];

function FAQItem({ q, a }: { q: string; a: React.ReactNode }) {
  return (
    <details className="border rounded overflow-hidden group" style={{ borderColor: 'var(--outline-variant)' }}>
      <summary className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors cursor-pointer list-none" style={{ backgroundColor: 'var(--surface-container)' }}>
        <span className="text-[14px] font-semibold leading-snug" style={{ color: 'var(--text-secondary)' }}>{q}</span>
        <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 transition-transform group-open:rotate-180">
          expand_more
        </span>
      </summary>
      <div className="px-5 pb-5 pt-1" style={{ backgroundColor: 'var(--surface-container-low)', borderTop: '1px solid var(--surface-container-high)' }}>
        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{a}</p>
      </div>
    </details>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-base)' }}>
      <Helmet>
        <title>Azure Pricing FAQ | AzureCalc.uk</title>
        <meta name="description" content="Answers to common questions about Azure pricing on AzureCalc.uk. How accurate are estimates, why bills differ, coverage, data sources, and more." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/faq" />
        <meta property="og:title" content="Azure Pricing FAQ | AzureCalc.uk" />
        <meta property="og:description" content="Common questions about Azure pricing and how AzureCalc.uk works." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/faq" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: typeof faq.a === 'string' ? faq.a : faq.q,
            },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([
          { name: 'Home', url: '/', position: 1 },
          { name: 'FAQ', url: '/faq', position: 2 },
        ]))}</script>
      </Helmet>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-[28px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-[15px]" style={{ color: 'var(--text-tertiary)' }}>
            Common questions about Azure pricing and how AzureCalc.uk works.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {faqs.map((faq) => (
            <FAQItem key={faq.q} q={faq.q} a={faq.a} />
          ))}
        </div>

        <div className="mt-12 rounded p-6 text-center" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <span className="material-symbols-outlined text-[#0078d4] text-[28px] mb-3 block">
            help
          </span>
          <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Still have a question?</p>
          <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
            Use the feedback button on any calculator, or browse the{' '}
            <Link to="/guides/azure-log-analytics-pricing-uk" style={{ color: 'var(--text-tertiary)' }}>
              pricing guides
            </Link>{' '}
            for detailed explanations.
          </p>
        </div>

        <div className="mt-8 p-4 border rounded text-[11px] leading-relaxed" style={{ borderColor: 'var(--surface-container-high)', color: 'var(--text-tertiary)' }}>
          DISCLAIMER: Cost estimates provided by this tool are approximations only and are not
          intended as actual price quotes. Prices are sourced from the Microsoft Azure Retail
          Prices API and are updated nightly. This tool is not affiliated with, endorsed by, or
          sponsored by Microsoft Corporation.
        </div>
      </div>
    </div>
  );
}
